import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Cube,
  ArrowRight,
  Stack,
  CalendarBlank,
  Drop,
  Truck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const HIGHLIGHTS = [
  [Stack, "Slab & remnant stock", "Every slab, offcut and hold in one place"],
  [
    CalendarBlank,
    "Jobs & schedule",
    "Template, fabrication and install stops by day",
  ],
  [Drop, "Sinks & suppliers", "Reserve sinks to jobs and reorder in a click"],
  [Truck, "Purchase orders", "Receive straight into inventory"],
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    api
      .get("/public/branding")
      .then(({ data }) => setLogo(data.logo))
      .catch(() => setLogo(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const mark = logo ? (
    <img
      src={`${process.env.REACT_APP_BACKEND_URL}/api/public/logo`}
      alt="Logo"
      data-testid="login-logo"
      className="h-12 w-12 rounded-xl border border-zinc-800 object-cover"
    />
  ) : (
    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-indigo-400/30 bg-indigo-500/15 text-indigo-300">
      <Cube size={26} weight="bold" />
    </span>
  );

  return (
    <div className="grain relative min-h-screen lg:grid lg:grid-cols-[1.1fr_1fr]">
      {/* Left: brand panel */}
      <div className="relative hidden overflow-hidden border-r border-zinc-800 lg:block">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/7739881/pexels-photo-7739881.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            {mark}
            <span className="text-sm font-bold uppercase tracking-[0.2em]">
              Brioni Granite
            </span>
          </div>
          <div>
            <h2 className="max-w-md text-4xl font-bold leading-tight tracking-tight">
              The whole shop floor, one screen.
            </h2>
            <div className="mt-10 grid max-w-lg gap-3 sm:grid-cols-2">
              {HIGHLIGHTS.map(([Icon, title, sub]) => (
                <div
                  key={title}
                  className="rounded-lg border border-zinc-800 bg-zinc-900/70 p-4 backdrop-blur"
                >
                  <Icon size={18} weight="bold" className="text-indigo-300" />
                  <div className="mt-2 text-sm font-semibold">{title}</div>
                  <div className="mt-0.5 text-xs text-zinc-500">{sub}</div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-700">
            Warehouse operations · Brioni Granite
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="relative z-10 flex min-h-screen items-center justify-center px-6 py-14">
        <div className="w-full max-w-sm">
          <div className="lg:hidden">{mark}</div>
          <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-zinc-600">
            Welcome back
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
            Sign in
          </h1>
          <p className="mt-2 text-sm text-zinc-500">
            Use the account your shop admin set up for you.
          </p>

          <form
            onSubmit={submit}
            className="mt-8 space-y-4"
            data-testid="login-form"
          >
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">
                Email
              </Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 border-zinc-800 bg-zinc-900 text-base focus-visible:ring-indigo-500"
                placeholder="you@shop.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">
                Password
              </Label>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 border-zinc-800 bg-zinc-900 text-base focus-visible:ring-indigo-500"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p
                data-testid="login-error"
                className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-300"
              >
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="group h-12 w-full bg-indigo-600 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-500"
            >
              {busy ? "Signing in…" : "Sign In"}
              <ArrowRight
                size={16}
                weight="bold"
                className="ml-2 transition-transform duration-200 group-hover:translate-x-1"
              />
            </Button>
          </form>

          <p className="mt-8 text-xs text-zinc-600">
            Forgot your password? Ask your shop admin to reset it from the Crew
            page.
          </p>
        </div>
      </div>
    </div>
  );
}
